import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Radio, RadioGroup, FormControlLabel, Select, MenuItem,
  CircularProgress, Box, Button, TextField, Alert, Chip
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import api from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { formatDateDisplay } from '../../utils/dateUtils';
import BSDateInput from '../common/BSDateInput';

const DespatchForm = () => {
  const [rows, setRows] = useState([]);
  const [karigars, setKarigars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const navigate = useNavigate();

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersRes, directOrdersRes, karigarsRes] = await Promise.all([
        api.get('/api/shop/orders'),
        api.get('/api/directorders'),
        api.get('/api/karigar')
      ]);

      // Karigar despatch: items not yet karigar-despatched (despatchStatus is karigar's flag)
      const orderRows = ordersRes.data.flatMap(order =>
        (order.items || [])
          .filter(item => !item.despatchStatus)
          .map(item => ({
            billName: order.billName,
            billNumber: order.billNumber,
            orderDate: formatDateDisplay(order.orderDate),
            deliveryDate: formatDateDisplay(order.deliveryDate),
            requiredDate: formatDateDisplay(order.requiredDate),
            orPriority: order.isUrgent ? 'Urgent' : 'Normal',
            salesman: order.agentName,
            itemName: item.itemName,
            itemQty: item.quantity,
            quantityUnit: item.unit,
            itemWeight: item.itemWeight,
            karat: item.karat,
            despatch: false,
            karigar: '',
            wastage: item.wastage || '',
            orderId: order._id,
            itemId: item._id,
            isDirect: false
          }))
      );

      const directOrderRows = directOrdersRes.data.flatMap(order =>
        (order.items || [])
          .filter(item => !item.despatchStatus)
          .map(item => ({
            billName: order.billName || 'D',
            billNumber: order.billNumber,
            orderDate: formatDateDisplay(order.orderDate),
            deliveryDate: formatDateDisplay(order.deliveryDate),
            requiredDate: formatDateDisplay(order.requiredDate),
            orPriority: order.isUrgent ? 'Urgent' : 'Normal',
            salesman: order.agentName || '',
            itemName: item.itemName,
            itemQty: item.quantity,
            quantityUnit: item.unit,
            itemWeight: item.itemWeight,
            karat: item.karat,
            despatch: false,
            karigar: '',
            wastage: item.wastage || '',
            orderId: order._id,
            itemId: item._id,
            isDirect: true
          }))
      );

      setRows([...orderRows, ...directOrderRows]);
      setKarigars(karigarsRes.data);
    } catch (err) {
      setError('Failed to fetch data');
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleDespatchChange = (index, value) => {
    setRows(prev => prev.map((row, i) => i === index ? { ...row, despatch: value === 'yes' } : row));
  };

  const handleKarigarChange = (index, value) => {
    setRows(prev => prev.map((row, i) => i === index ? { ...row, karigar: value } : row));
  };

  const handleWastageChange = (index, value) => {
    setRows(prev => prev.map((row, i) => i === index ? { ...row, wastage: value } : row));
  };

  const selectedRows = rows.filter(r => r.despatch && r.karigar);

  const filteredRows = rows.filter(row => {
    if (!dateFrom && !dateTo) return true;
    const d = row.orderDate;
    if (!d) return true;
    if (dateFrom && d < dateFrom) return false;
    if (dateTo && d > dateTo) return false;
    return true;
  });

  const handleDespatchAll = async () => {
    if (selectedRows.length === 0) return;
    setSaving(true);
    setError('');
    try {
      const groups = {};
      selectedRows.forEach(row => {
        const key = `${row.isDirect ? 'd' : 'o'}_${row.orderId}`;
        if (!groups[key]) groups[key] = { row, itemIds: [] };
        groups[key].itemIds.push(row.itemId);
      });

      for (const { row, itemIds } of Object.values(groups)) {
        const endpoint = row.isDirect
          ? `/api/directorders/${row.orderId}`
          : `/api/shop/orders/${row.orderId}`;
        const orderRes = await api.get(endpoint);
        const order = orderRes.data;
        const updatedItems = order.items.map(item => {
          if (itemIds.includes(String(item._id))) {
            const matchRow = selectedRows.find(r => String(r.itemId) === String(item._id) && String(r.orderId) === String(row.orderId));
            return {
              ...item,
              despatchStatus: true,
              karigarAbbreviation: matchRow?.karigar || row.karigar,
              wastage: matchRow?.wastage || row.wastage
            };
          }
          return item;
        });
        await api.put(endpoint, { ...order, items: updatedItems });
      }

      setSuccess(`${selectedRows.length} item(s) despatched successfully.`);
      await fetchData();
    } catch (err) {
      setError('Failed to despatch items');
    }
    setSaving(false);
  };

  if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh"><CircularProgress /></Box>;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="outlined" color="primary" sx={{ mb: 2 }}>
          Back
        </Button>

        <Typography variant="h4" color="primary" sx={{ mb: 2 }}>Karigar Despatch</Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          <BSDateInput label="Order Date From" value={dateFrom} onChange={setDateFrom} />
          <BSDateInput label="Order Date To" value={dateTo} onChange={setDateTo} />
          {(dateFrom || dateTo) && (
            <Button size="small" onClick={() => { setDateFrom(''); setDateTo(''); }}>Clear</Button>
          )}
        </Box>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Bill', 'Order Date', 'Delivery', 'Required', 'Priority', 'Salesman', 'Item', 'Qty', 'Wt(g)', 'Karat', 'Despatch?', 'Karigar', 'Wastage%'].map(h => (
                  <TableCell key={h} sx={{ fontSize: '0.75rem', p: 0.5, fontWeight: 600 }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRows.map((row, idx) => (
                <TableRow
                  key={`${row.orderId}-${row.itemId}`}
                  sx={{ backgroundColor: row.despatch ? '#f0fff4' : 'inherit' }}
                >
                  <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{row.billName} {row.billNumber}</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{row.orderDate}</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{row.deliveryDate}</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{row.requiredDate}</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{row.orPriority}</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{row.salesman}</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{row.itemName}</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{row.itemQty} {row.quantityUnit}</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{row.itemWeight}</TableCell>
                  <TableCell sx={{ fontSize: '0.7rem', p: 0.5 }}>{row.karat}</TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    <RadioGroup
                      row
                      value={row.despatch ? 'yes' : 'no'}
                      onChange={e => handleDespatchChange(idx, e.target.value)}
                      sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.7rem' } }}
                    >
                      <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                      <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
                    </RadioGroup>
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    {row.despatch ? (
                      <Select
                        size="small"
                        value={row.karigar}
                        onChange={e => handleKarigarChange(idx, e.target.value)}
                        displayEmpty
                        renderValue={s => s || 'Select'}
                        sx={{ minWidth: 110, fontSize: '0.75rem' }}
                      >
                        <MenuItem value=""><em>Select</em></MenuItem>
                        {karigars.map(k => (
                          <MenuItem key={k.abbreviation} value={k.abbreviation} sx={{ fontSize: '0.75rem' }}>
                            {k.abbreviation} - {k.name}
                          </MenuItem>
                        ))}
                      </Select>
                    ) : <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>—</Typography>}
                  </TableCell>
                  <TableCell sx={{ p: 0.5 }}>
                    {row.despatch ? (
                      <TextField
                        value={row.wastage}
                        onChange={e => handleWastageChange(idx, e.target.value)}
                        size="small"
                        sx={{ width: 70 }}
                        inputProps={{ style: { fontSize: '0.75rem' } }}
                        placeholder="%"
                      />
                    ) : <Typography sx={{ fontSize: '0.7rem', color: 'text.disabled' }}>—</Typography>}
                  </TableCell>
                </TableRow>
              ))}
              {filteredRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={13} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    No pending items to despatch
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2, mt: 2 }}>
          {selectedRows.length > 0 && (
            <Chip label={`${selectedRows.length} item(s) marked Yes`} color="primary" size="small" />
          )}
          <Button
            variant="contained"
            color="success"
            disabled={selectedRows.length === 0 || saving}
            onClick={handleDespatchAll}
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default DespatchForm;
